import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import BuyCreditsModal, { SUBSCRIPTION, TOPUP } from "@/components/UpgradeModal";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Check, RefreshCw, Zap, Loader2 } from "lucide-react";

const fmt = (n) => n.toLocaleString("it-IT");

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [showBuy, setShowBuy] = useState(false);
  const [loading, setLoading] = useState(null);
  const subscribed = user?.sub_status === "active";

  const buy = async (key) => {
    if (!user) return nav("/register");
    setLoading(key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setLoading(null);
    }
  };

  const subLabel = () => {
    if (subscribed) return "Abbonamento attivo";
    if (loading === SUBSCRIPTION.key) return <Loader2 className="h-4 w-4 animate-spin" />;
    return user ? "Abbonati" : "Inizia gratis";
  };

  const topupLabel = () => {
    if (loading === TOPUP.key) return <Loader2 className="h-4 w-4 animate-spin" />;
    return user ? "Acquista" : "Inizia gratis";
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-[1000px] mx-auto px-4 md:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter">
            Un abbonamento semplice, <span className="text-brand-blue">battute extra</span> quando servono.
          </h1>
          <p className="text-muted-foreground mt-4">
            90.000 battute al mese con l'abbonamento. Se le finisci, aggiungi pacchetti extra che non scadono.
          </p>
          {!user && <p className="text-sm text-brand-pink mt-3">Registrati e ricevi 2.000 battute gratis per provare.</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            data-testid="pricing-sub_monthly"
            className="relative rounded-3xl border border-brand-pink/50 bg-card p-8 flex flex-col overflow-hidden">
            <div className="absolute -top-32 -right-16 h-64 w-64 rounded-full bg-brand-pink/15 blur-3xl pointer-events-none" />
            <span className="absolute top-6 right-6 text-[10px] font-mono-label uppercase tracking-wider bg-brand-pink/15 text-brand-pink px-2.5 py-1 rounded-full flex items-center gap-1">
              <RefreshCw className="h-3 w-3" /> Mensile
            </span>
            <div className="relative">
              <h3 className="font-heading text-2xl font-medium">{SUBSCRIPTION.label}</h3>
              <p className="text-sm text-brand-blue font-medium mt-1">{fmt(SUBSCRIPTION.chars)} battute / mese</p>
              <div className="mt-6 mb-6">
                <span className="font-heading text-4xl font-semibold">{SUBSCRIPTION.price}</span>
                <span className="text-muted-foreground text-sm"> / mese</span>
              </div>
              <ul className="space-y-3 mb-8">
                {["90.000 battute ogni mese", "Testo e documenti (PDF/Word/TXT)", "Tutti i toni di voce", "Si rinnova automaticamente", "Disdici quando vuoi"].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm"><Check className="h-4 w-4 mt-0.5 shrink-0 text-brand-pink" /> {f}</li>
                ))}
              </ul>
              <Button data-testid="buy-sub_monthly" onClick={() => buy(SUBSCRIPTION.key)} disabled={loading !== null || subscribed}
                className="w-full rounded-full h-11 mt-auto bg-brand-pink hover:bg-brand-pink/90 text-white">
                {subLabel()}
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            data-testid="pricing-topup_15k"
            className="relative rounded-3xl border border-border bg-card p-8 flex flex-col">
            <span className="absolute top-6 right-6 text-[10px] font-mono-label uppercase tracking-wider bg-brand-blue/15 text-brand-blue px-2.5 py-1 rounded-full flex items-center gap-1">
              <Zap className="h-3 w-3" /> Una tantum
            </span>
            <h3 className="font-heading text-2xl font-medium">{TOPUP.label}</h3>
            <p className="text-sm text-brand-blue font-medium mt-1">{fmt(TOPUP.chars)} battute</p>
            <div className="mt-6 mb-6">
              <span className="font-heading text-4xl font-semibold">{TOPUP.price}</span>
              <span className="text-muted-foreground text-sm"> una tantum</span>
            </div>
            <ul className="space-y-3 mb-8">
              {["15.000 battute extra", "Le battute non scadono mai", "Ideale quando finisci il mese", "Cumulabile con l'abbonamento"].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm"><Check className="h-4 w-4 mt-0.5 shrink-0 text-brand-blue" /> {f}</li>
              ))}
            </ul>
            <Button data-testid="buy-topup_15k" onClick={() => buy(TOPUP.key)} disabled={loading !== null}
              className="w-full rounded-full h-11 mt-auto bg-brand-blue hover:bg-brand-blue/90 text-white">
              {topupLabel()}
            </Button>
          </motion.div>
        </div>
      </div>
      <BuyCreditsModal open={showBuy} onOpenChange={setShowBuy} />
    </div>
  );
}
